/**
 * The circuits of a plant — the basins an Aquatouch drives side by side, each
 * with its own menu, start screen and messages.
 *
 * Which circuit is shown follows from the address: every screen key of a
 * circuit starts with its prefix (`unit2/chlorineSetpoints`), so a jump from a
 * message, a chip or a card into another basin switches the circuit on its own,
 * and the browser's back button switches it back. Only an address that belongs
 * to no circuit (an app page, a plant-wide screen) keeps the one seen last.
 *
 * Pure functions over the configuration; `composables/useCircuit.ts` holds the
 * reactive state around them.
 */
import type { ISystem } from '@/interfaces/gui'

/** The app route of the plant overview; resolved by its last segment like every app page. */
export const OVERVIEW_ROUTE = 'circuitOverview'

function firstSegment(route: string): string {
  const path = route.startsWith('/') ? route.slice(1) : route
  return path.split('/')[0] || ''
}

/**
 * The first segment of every screen key that belongs to the circuit: its prefix,
 * else the segment in front of its start screen, else its name.
 */
export function circuitKey(circuit: ISystem): string {
  const start = circuit.startscreen ?? ''
  return circuit.prefix || (start.includes('/') ? firstSegment(start) : '') || circuit.name
}

/** The circuit a route belongs to, undefined for routes outside every circuit. */
export function circuitOfRoute(
  route: string,
  circuits: ReadonlyArray<ISystem>,
): ISystem | undefined {
  const segment = firstSegment(route)
  return segment ? circuits.find((circuit) => circuitKey(circuit) === segment) : undefined
}

/** The circuit of the route, else the one seen last, else the first of the plant. */
export function resolveCircuit(
  route: string,
  circuits: ReadonlyArray<ISystem>,
  lastName?: string,
): ISystem | undefined {
  return (
    circuitOfRoute(route, circuits) ??
    circuits.find((circuit) => circuit.name === lastName) ??
    circuits[0]
  )
}

/** A plant overview is worth showing when there is more than one basin to compare. */
export function hasPlantOverview(circuits: ReadonlyArray<ISystem>): boolean {
  return circuits.length > 1 && circuits.some((circuit) => (circuit.overview?.length ?? 0) > 0)
}

/** Where the app opens: the plant overview, or the start screen of the first circuit. */
export function startRoute(circuits: ReadonlyArray<ISystem>): string | undefined {
  if (hasPlantOverview(circuits)) return `/${OVERVIEW_ROUTE}`
  const first = circuits[0]
  return first?.startscreen ? `/${first.startscreen}` : undefined
}

/** An app page inside the circuit, so the circuit stays in force while it is open. */
export function circuitPageRoute(circuit: ISystem | undefined, page: string): string {
  return circuit ? `/${circuitKey(circuit)}/${page}` : `/${page}`
}
